/**
 * FunctionKeys — MDT F-key row (F1-F8) extracted from CrewLoginPage.js (iter 68).
 *
 * Pure presentational — parent computes `functionKeys` config + owns the
 * gating helper `canCrewChangeSignal` and the click handler `onPress(fk)`.
 * This component only renders button state (active / disabled / timestamp).
 */
import React from "react";

const CALL_SIGNALS = [10, 63, 84, 82, 81];

export function FunctionKeys({
  functionKeys,
  loggedIn,
  currentSignal,
  hospitalChoice,
  onCall,
  assignedCall,
  signalTimestamps,
  signalTimestamp,
  canCrewChangeSignal,
  onPress,
}) {
  return (
    <div className="grid grid-cols-9 border-t border-cyan-400">
      {functionKeys.map((fk) => {
        const isActive = loggedIn && currentSignal === fk.signal;
        // HA/HB share signal 82 but should only light up for whichever key
        // was actually pressed by the crew.
        const isHAorHB = fk.code === "HA" || fk.code === "HB";
        const isActiveHAorHB = loggedIn && currentSignal === 82 && hospitalChoice === fk.code;
        const isGeoOnly = fk.geoOnly;
        const cantGoForward = fk.callOnly && !onCall;
        const cantGoBack = fk.signal && !canCrewChangeSignal(fk.signal);
        // HA/HB must stay clickable while in transport (signal 82) so the
        // crew can change hospitals up until they hit 81.
        const cantGoBackHA = isHAorHB && currentSignal !== 82 && !canCrewChangeSignal(82);
        // OOS / FLAGGED DOWN calls (auto_on_scene) go 84 → 98 directly —
        // disable 82 / 81 / HA / HB so the crew can't accidentally try.
        const isOosCall = !!assignedCall?.job?.auto_on_scene;
        const oosDisabled =
          isOosCall && (fk.signal === 82 || fk.signal === 81 || isHAorHB);
        const isDisabled =
          loggedIn && (isGeoOnly || cantGoForward || cantGoBack || cantGoBackHA || oosDisabled);

        // Call-signal keys show every timestamp accumulated during THIS call.
        // Available-signal keys (97/98/99) only show time when they're active.
        const callSignalTs = CALL_SIGNALS.includes(fk.signal) ? signalTimestamps[fk.signal] : null;
        const haTs = isHAorHB ? signalTimestamps[fk.code] : null;
        const activeVisually = isActive || isActiveHAorHB;
        const showTs = callSignalTs || haTs || (activeVisually && signalTimestamp);

        // Active button per operator spec: green bg + blue text.
        const activeClasses = "bg-[#008f00] text-[#1d39ff] cursor-pointer";

        return (
          <div
            key={fk.key}
            data-testid={`fn-${fk.key}`}
            onClick={() => onPress(fk)}
            className={[
              "h-[70px] border-r border-cyan-400 last:border-r-0 flex flex-col items-center justify-center font-bold leading-4",
              isDisabled
                ? "bg-[#5a5a5a] text-[#888] cursor-not-allowed"
                : activeVisually
                ? activeClasses
                : (callSignalTs || haTs)
                ? "bg-[#8a8a8a] text-[#d8ffd8] cursor-pointer"
                : "bg-[#8a8a8a] text-[#d8d8d8] cursor-pointer",
            ].join(" ")}
          >
            <div className="text-[18px]">{fk.code}</div>
            <div className="text-[12px]">{fk.key}</div>
            {showTs && (
              <div className={`text-[14px] mt-[2px] font-bold ${activeVisually ? "" : "text-white"}`}>
                {callSignalTs || haTs || signalTimestamp}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default FunctionKeys;
